import React, {
  createContext,
  useMemo,
  useState,
  ReactNode,
  useContext,
  useCallback,
  useRef,
} from "react";
import {connect} from "react-redux";

import {
  AnyDataPath,
  Facility,
  IDateRangeGlobal,
} from "../../store/reporting/reporting.common";
import {FileExtensionItem} from "../../atoms/reporting-block/file-export-dropdown-select/FileExportDropdownSelect";
import {select} from "../../store";
import {setActiveFacilities} from "../../store/reporting/reporting.actions";
import {DateRangePresets} from "../../atoms/reporting-compare";

const DEFAULT_DATE_RANGE_VALUE = 1;

type OwnProps = Readonly<{
  children: ReactNode;
}>;

type StateProps = Readonly<{
  activeFacilitiesIds: string[] | null;
}>;

type DispatchProps = Readonly<{
  setActiveFacilities: typeof setActiveFacilities;
}>;

type Props = OwnProps & StateProps & DispatchProps;

export type ExportControlsRegistration = Readonly<{
  onExport: (item: FileExtensionItem) => void;
  dataPath: AnyDataPath;
  hasData?: boolean;
}>;

export type RegisterExportControlsOptions = Readonly<{
  ownerId: string;
  /**
   * `replace` — always wins (single-chart pages, full report).
   * `first-wins` — only the first owner on a multi-chart section list page.
   */
  mode?: "replace" | "first-wins";
}>;

export type Context = Readonly<{
  /**
   * Boolean used to show/hide full page spinner when the PDF export is in progress.
   */
  pageLoading: boolean;
  setPageLoading: (pageLoading: boolean) => void;
  baseDateRange: IDateRangeGlobal;
  activeFacilitiesIds: Facility[];
  setActiveFacilities: typeof setActiveFacilities;
  setBaseDateRange: (range: IDateRangeGlobal) => void;
  selectedWorkflowLabel: string | null;
  setSelectedWorkflowLabel: (label: string | null) => void;
  exportControls: ExportControlsRegistration | null;
  registerExportControls: (
    controls: ExportControlsRegistration,
    options: RegisterExportControlsOptions,
  ) => void;
  clearExportControls: (ownerId: string) => void;
}>;

const ReportContext = createContext<Context>(undefined);

type ConsumerProps = Readonly<{
  children: (context: Context) => ReactNode;
}>;

export const useReportContext = () => useContext(ReportContext);

export const ReportContextConsumer = ({children}: ConsumerProps) => (
  <ReportContext.Consumer>
    {context => {
      if (context === undefined) {
        throw new Error(
          "ReportContextConsumer must be used within the ReportContextProvider",
        );
      }

      return children(context);
    }}
  </ReportContext.Consumer>
);

export const ReportContextProvider = ({
  children,
  activeFacilitiesIds,
  setActiveFacilities,
}: Props) => {
  const [pageLoading, setPageLoading] = useState(false);
  const [selectedWorkflowLabel, setSelectedWorkflowLabel] = useState<
    string | null
  >(null);
  const [exportControls, setExportControls] =
    useState<ExportControlsRegistration | null>(null);
  const exportOwnerIdRef = useRef<string | null>(null);
  const [baseDateRange, setBaseDateRange] = useState<IDateRangeGlobal>({
    ...DateRangePresets[DEFAULT_DATE_RANGE_VALUE],
    to: DateRangePresets[DEFAULT_DATE_RANGE_VALUE].to(),
    from: DateRangePresets[DEFAULT_DATE_RANGE_VALUE].from(),
  });

  const registerExportControls = useCallback(
    (
      controls: ExportControlsRegistration,
      {ownerId, mode = "replace"}: RegisterExportControlsOptions,
    ) => {
      if (
        mode === "first-wins" &&
        exportOwnerIdRef.current &&
        exportOwnerIdRef.current !== ownerId
      ) {
        return;
      }

      exportOwnerIdRef.current = ownerId;
      setExportControls(controls);
    },
    [],
  );

  const clearExportControls = useCallback((ownerId: string) => {
    if (exportOwnerIdRef.current !== ownerId) {
      return;
    }

    exportOwnerIdRef.current = null;
    setExportControls(null);
  }, []);

  const value = useMemo(
    () => ({
      pageLoading,
      setPageLoading,
      baseDateRange,
      activeFacilitiesIds,
      setActiveFacilities,
      setBaseDateRange,
      selectedWorkflowLabel,
      setSelectedWorkflowLabel,
      exportControls,
      registerExportControls,
      clearExportControls,
    }),
    [
      pageLoading,
      baseDateRange,
      activeFacilitiesIds,
      setActiveFacilities,
      setBaseDateRange,
      selectedWorkflowLabel,
      exportControls,
      registerExportControls,
      clearExportControls,
    ],
  );

  return (
    <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
  );
};

export const mapStateToProps = (state: any) => ({
  activeFacilitiesIds: select.adminSession.activeFacilitiesIds(state),
});

export const mapDispatchToProps = (dispatch: any) => ({
  reset: dispatch.adminSession.reset,
  setActiveFacilities: (payload: Parameters<typeof setActiveFacilities>[0]) =>
    dispatch.adminSession.setActiveFacilities(payload),
});

export const ConnectedReportContextProvider = connect<
  StateProps,
  DispatchProps,
  OwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(ReportContextProvider);
